import React, { useEffect, useState } from 'react';
import { Modal, Box, IconButton, CircularProgress, Typography, Backdrop, Chip, Paper, Divider, Grid, Link, FormControlLabel, Checkbox, Avatar, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import NotesIcon from '@mui/icons-material/Notes';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupsIcon from '@mui/icons-material/Groups';
import CategoryIcon from '@mui/icons-material/Category';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format, parseISO } from 'date-fns';
import { Gathering, GatheringStatus } from '../../../types';
import { fetchGatheringById } from '../../../services/api/endpoints/gatherings';
import { getGatheringManageUrl, getGatheringCheckInUrl } from '../../../utils/environment';
import GatheringDescription from '../../../shared/components/GatheringDescription';

interface GatheringInfoModalProps {
  gatheringId: string | null;
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
}

// Style for the modal content - smaller than the full editing modal
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 3 },
  borderRadius: 2,
  outline: 0,
  overflow: 'auto'
};

// Status color mapping
const statusColorMap: Record<GatheringStatus, string> = {
  [GatheringStatus.NEW]: '#3498db',
  [GatheringStatus.IN_REVIEW]: '#f39c12',
  [GatheringStatus.NEED_MORE_INFO]: '#e74c3c',
  [GatheringStatus.RESCHEDULE]: '#9b59b6',
  [GatheringStatus.DENIED]: '#e74c3c',
  [GatheringStatus.AWAITING_GATHERING_LINK]: '#2ecc71',
  [GatheringStatus.READY_TO_PUBLISH]: '#27ae60',
  [GatheringStatus.PUBLISHED]: '#2ecc71'
};

const GatheringInfoModal: React.FC<GatheringInfoModalProps> = ({
  gatheringId,
  isOpen,
  onClose,
  token
}) => {
  const [gathering, setGathering] = useState<Gathering | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && gatheringId && token) {
      setLoading(true);
      setError(null);
      
      fetchGatheringById(token, gatheringId)
        .then(data => {
          setGathering(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching gathering details:', err);
          setError('Failed to load gathering details. Please try again.');
          setLoading(false);
        });
    } else {
      // Reset state when modal closes
      setGathering(null);
    }
  }, [gatheringId, isOpen, token]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = parseISO(dateString);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (timeString: string): string => {
    if (!timeString) return 'N/A';
    
    try {
      // Check if the time already has AM/PM
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }
      
      // Assuming time is in format like "19:00:00"
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      
      return `${formattedHour}:${minutes} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration for display
  const formatDuration = (minutes: number): string => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} mins`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    } else {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMinutes} mins`;
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="gathering-info-modal"
      aria-describedby="read-only view of gathering details"
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Box sx={modalStyle}>
        {/* Close button in top-right corner */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'grey.500',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: 'grey.800',
            },
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            flexDirection: 'column',
            minHeight: '200px',
            my: 4 
          }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading gathering details...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ 
            color: 'error.main', 
            textAlign: 'center', 
            my: 4,
            p: 3,
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 1,
            bgcolor: 'error.lightest'
          }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error
            </Typography>
            <Typography variant="body1">{error}</Typography>
          </Box>
        ) : gathering ? (
          <Box sx={{ width: '100%' }}>
            {/* Header with title and status */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 1, pr: 6 }}>
                {gathering.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={gathering.status} 
                  sx={{ 
                    bgcolor: statusColorMap[gathering.status as GatheringStatus] || 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    py: 0.5
                  }} 
                />
                <Typography variant="body2" color="text.secondary">
                  ID: {gathering.id}
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 3,
              pt: 1
            }}>
              <Tooltip 
                title={gathering.source !== 'web' ? 'External gathering: No web app available' : ''}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(getGatheringManageUrl(gathering.id), '_blank', 'noopener,noreferrer')}
                    disabled={gathering.source !== 'web'}
                  >
                    Manage
                  </Button>
                </span>
              </Tooltip>
              <Tooltip 
                title={gathering.source !== 'web' ? 'External gathering: No web app available' : ''}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="medium"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(getGatheringCheckInUrl(gathering.id), '_blank', 'noopener,noreferrer')}
                    disabled={gathering.source !== 'web'}
                  >
                    Check In
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Schedule Information Section */}
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 2
              }}
            >
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                Schedule Information
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                {/* Date and Time */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(gathering.date)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Start Time</Typography>
                      </Box>
                      <Typography variant="body1">
                        {formatTime(gathering.start_time)}
                        {gathering.space?.timezone && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({gathering.space.timezone})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">End Time</Typography>
                      </Box>
                      <Typography variant="body1">
                        {formatTime(gathering.end_time)}
                      </Typography>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                      </Box>
                      <Typography variant="body1">
                        {formatDuration(gathering.duration)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Space and Areas */}
                <Grid size={{ xs: 12, md: 6 }}>
                  {/* Space */}
                  {gathering.space && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Space</Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="medium">
                        {gathering.space.name}
                      </Typography>
                    </Box>
                  )}

                  {/* Areas */}
                  {gathering.areas && gathering.areas.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Areas</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {gathering.areas.map((area, index) => (
                          <Chip 
                            key={area.id} 
                            label={area.name} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                Event Details
              </Typography>
              
              <Grid container spacing={3}>
                {/* Image */}
                {gathering.image_url && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ 
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: { xs: 2, md: 0 },
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img 
                        src={gathering.image_url} 
                        alt={gathering.title} 
                        style={{ 
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    </Box>
                  </Grid>
                )}
                
                {/* Event Details */}
                <Grid size={{ xs: 12, md: gathering.image_url ? 8 : 12 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Community, Event Size, Category, Owner - All in one row */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      justifyContent: 'space-between', 
                      flexWrap: 'wrap',
                      mb: 1
                    }}>
                      {/* Community */}
                      <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <GroupsIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">Community</Typography>
                        </Box>
                        <Typography variant="body1">
                          {gathering.community || 'N/A'}
                        </Typography>
                      </Box>

                      {/* Event Size */}
                      <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <GroupIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">Event Size</Typography>
                        </Box>
                        <Typography variant="body1">{gathering.event_size || 'N/A'} people</Typography>
                      </Box>
                      
                      {/* Category */}
                      <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                        </Box>
                        <Typography variant="body1">{gathering.category || 'N/A'}</Typography>
                      </Box>
                      
                      {/* Owner */}
                      {gathering.owner && (
                        <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                            <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                mr: 1, 
                                fontSize: '0.75rem',
                                bgcolor: 'primary.main'
                              }}
                            >
                              {gathering.owner.display_name?.charAt(0) || 'U'}
                            </Avatar>
                            <Typography variant="body1">
                              {gathering.owner.display_name || 'Unknown'}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Gathering Link */}
                    {gathering.luma_link && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Gathering Link
                        </Typography>
                        <Link 
                          href={gathering.luma_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {gathering.luma_link}
                        </Link>
                      </Box>
                    )}
                    
                    {/* Flags */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_public} 
                            disabled 
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Public</Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_members_only} 
                            disabled 
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PeopleIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Members Only</Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_featured} 
                            disabled 
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Featured</Typography>
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                </Grid>
                
                {/* Divider between details and description/notes */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 3 }} />
                </Grid>
                
                {/* Description */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotesIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  </Box>
                  <GatheringDescription description={gathering.description} />
                </Grid>
                
                {/* Additional Notes */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotesIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Additional Notes</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {gathering.additional_notes || 'No additional notes.'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Modal>
  );
};

export default GatheringInfoModal;
